/**
 * Route table and the app shell.
 *
 *   /                 home
 *   /docs/:id         the Docs editor
 *   /sheets/:id       the Sheets editor
 *   /slides/:id       the Slides editor
 *   /l/:token         a link visitor's landing
 *
 * Three surfaces, and the split at the top of `App` is load-bearing: `/l/*` is a
 * guest's, and it renders **before the provider** because they have no Mauvely
 * account and must never be sent to a sign-in page. A document route is rendered
 * for a guest as well, once the landing has set their grant cookie, so unlike
 * home it does not treat `signedOut` as a reason to leave: the page asks the API
 * who it is.
 *
 * Home and an editor draw their own chrome (home's bar and rail, the editor's
 * floating glass), so nothing here wraps them.
 */

import { lazy, Suspense } from 'react';
import type React from 'react';
import { matchRoute, useRouter } from './router';
import { AppProvider, useApp } from './state';
import { Wordmark } from './components/Wordmark';
import { Home } from './pages/Home';
import { Editor } from './pages/Editor';
import { Button } from './core/controls';
import { AppKindProvider, ToastHost } from './core/ui';
import { DocumentSkeleton } from './pages/DocumentSkeleton';
import { HomeSkeleton } from './pages/HomeSkeleton';
import { DOC_KINDS, type DocKind } from '@shared/api';
import { signInHref } from './theme';
import { useEffect } from 'react';

// The guest's landing is its own chunk: Home never needs it, and it never needs Home.
const LinkLanding = lazy(() => import('./pages/LinkLanding').then((m) => ({ default: m.LinkLanding })));

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="mv-center">{children}</div>;
}

function SignedOut() {
  useEffect(() => {
    window.location.href = signInHref(window.location.href);
  }, []);
  return (
    <Centered>
      <Wordmark />
      <p className="mv-eyebrow">Taking you to sign in</p>
    </Centered>
  );
}

function SecondFactor() {
  return (
    <Centered>
      <Wordmark />
      <h1>Finish signing in.</h1>
      <p>Your account uses two-factor authentication.</p>
      <Button
        variant="suite"
        size="lg"
        onClick={() => {
          // /signin would mint another session in the same condition and loop.
          const next = encodeURIComponent(window.location.href);
          window.location.href = `${new URL(signInHref('/')).origin}/signin/two-factor?next=${next}`;
        }}
      >
        Continue
      </Button>
    </Centered>
  );
}

function Unreachable({ onRetry }: { onRetry: () => void }) {
  return (
    <Centered>
      <Wordmark />
      <h1>Office is not answering.</h1>
      <Button variant="suite" size="lg" onClick={onRetry}>
        Try again
      </Button>
    </Centered>
  );
}

function NotReady() {
  const { notReady } = useApp();
  return (
    <Centered>
      <Wordmark />
      <h1>This deployment is not finished.</h1>
      <p>{notReady?.message}</p>
      {notReady?.missingTables?.length ? (
        <p className="mv-eyebrow">Missing · {notReady.missingTables.join(' · ')}</p>
      ) : null}
      {notReady?.hint && <p style={{ color: 'var(--fg-4)', fontSize: 13.5 }}>{notReady.hint}</p>}
    </Centered>
  );
}

/** `/docs/:id`, `/sheets/:id` or `/slides/:id`, as a kind and an id. */
function matchDocument(path: string): { kind: DocKind; id: string } | null {
  for (const kind of DOC_KINDS) {
    const m = matchRoute(`/${kind}/:id`, path);
    if (m?.id) return { kind, id: m.id };
  }
  return null;
}

function Routes() {
  const { path } = useRouter();
  const { status, refresh } = useApp();

  // The outline of the page the reader is going to, which the page then lands on.
  if (status === 'loading') {
    const doc = matchDocument(path);
    return doc ? <DocumentSkeleton kind={doc.kind} /> : <HomeSkeleton />;
  }
  if (status === 'notReady') return <NotReady />;
  if (status === 'unreachable') return <Unreachable onRetry={() => void refresh()} />;

  // A document route is for a guest too, so a missing session is not a reason
  // to leave. See the header.
  const doc = matchDocument(path);
  if (doc) return <Editor kind={doc.kind} id={doc.id} />;

  if (status === 'signedOut') return <SignedOut />;
  if (status === 'secondFactor') return <SecondFactor />;

  return <Home />;
}

export function App() {
  const { path } = useRouter();

  /**
   * The guest's surface is chosen before the provider mounts, deliberately.
   *
   * Rendering it inside `AppProvider` would still fetch `/api/office/me` on the
   * way past, so every link opened with a 401 in the console of someone who has
   * no account and never will. A page that must not consult an account should
   * not be inside the thing that consults it.
   */
  const link = matchRoute('/l/:token', path);
  if (link) {
    return (
      <Suspense fallback={<DocumentSkeleton />}>
        <LinkLanding token={link.token!} />
      </Suspense>
    );
  }

  // The toast is drawn above every page, outside the editor's own AppKindProvider: it learns the
  // editor from the address, and so wears its theme (named, in Slides) and colour.
  return (
    <AppProvider>
      <AppKindProvider value={matchDocument(path)?.kind ?? null}>
        <ToastHost>
          <Routes />
        </ToastHost>
      </AppKindProvider>
    </AppProvider>
  );
}
